import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Send, Plus, Search, Users, User, MessageCircle, ArrowLeft, X, Check, CheckCheck, UserPlus, Lock
} from 'lucide-react';

// ── Conversation Sidebar Item ──
function ConversationItem({ conv, currentUserId, isActive, onClick }) {
  const displayName = conv.type === 'group'
    ? (conv.name || 'Group Chat')
    : Object.entries(conv.participant_names || {}).find(([id]) => id !== currentUserId)?.[1] || 'Unknown';

  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const timeAgo = conv.last_message_at ? formatTime(conv.last_message_at) : '';

  return (
    <button
      onClick={onClick}
      data-testid={`conversation-${conv.id}`}
      className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left btn-lift ${
        isActive ? 'bg-[#1B4332] text-white' : 'hover:bg-[#E9EFE8]'
      }`}
    >
      <div className={`w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold ${
        isActive ? 'bg-white/20 text-white' : 'bg-[#1B4332]/10 text-[#1B4332]'
      }`}>
        {conv.type === 'group' ? <Users size={18} /> : initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`font-semibold text-sm truncate ${isActive ? 'text-white' : 'text-[#122A1F]'}`}>
            {displayName}
          </span>
          <span className={`text-[10px] flex-shrink-0 ${isActive ? 'text-white/70' : 'text-[#4B6B58]'}`}>{timeAgo}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs truncate ${isActive ? 'text-white/70' : 'text-[#4B6B58]'}`}>
            {conv.last_message || 'No messages yet'}
          </span>
          {conv.unread_count > 0 && !isActive && (
            <span className="w-5 h-5 rounded-full bg-[#1B4332] text-white text-[10px] flex items-center justify-center flex-shrink-0">
              {conv.unread_count > 9 ? '9+' : conv.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Message Bubble ──
function MessageBubble({ message, isOwn, showName }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2`} data-testid={`message-${message.id}`}>
      <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        {showName && !isOwn && (
          <span className="text-[10px] font-medium text-[#4B6B58] ml-3 mb-0.5 block">{message.sender_name}</span>
        )}
        <div className={`px-4 py-2.5 rounded-[1.25rem] text-sm leading-relaxed ${
          isOwn
            ? 'bg-[#1B4332] text-white rounded-br-md'
            : 'bg-white text-[#122A1F] rounded-bl-md shadow-[0_2px_8px_rgb(27,67,50,0.06)]'
        }`}>
          {message.content}
        </div>
        <span className={`text-[10px] mt-0.5 block ${isOwn ? 'text-right mr-1' : 'ml-3'} text-[#4B6B58]`}>
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  );
}

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Main ChatPage ──
export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { authHeaders, API, user, hasFullAccess } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [searchConvs, setSearchConvs] = useState('');

  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newChatTab, setNewChatTab] = useState('direct');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [searching, setSearching] = useState(false);

  const [mobileShowChat, setMobileShowChat] = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const convPollRef = useRef(null);

  const isSubscriber = hasFullAccess;

  // ── Fetch conversations ──
  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conversations`, authHeaders());
      setConversations(res.data);
    } catch (err) {
      if (err.response?.status === 403) return; // not subscribed
    } finally {
      setLoadingConvs(false);
    }
  }, [API, authHeaders]);

  // ── Fetch messages ──
  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/conversations/${convId}/messages?limit=100`, authHeaders());
      setMessages(res.data);
    } catch (err) {
      // silent
    }
  }, [API, authHeaders]);

  // ── Initial load ──
  useEffect(() => {
    if (isSubscriber) fetchConversations();
  }, [isSubscriber, fetchConversations]);

  // ── Set active conversation from URL ──
  useEffect(() => {
    if (conversationId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        setActiveConv(conv);
        setMobileShowChat(true);
      }
    }
  }, [conversationId, conversations]);

  // ── Load messages when active conversation changes ──
  useEffect(() => {
    if (activeConv) {
      setLoadingMsgs(true);
      fetchMessages(activeConv.id).finally(() => setLoadingMsgs(false));
    }
  }, [activeConv, fetchMessages]);

  // ── Scroll to bottom on new messages ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Poll for new messages ──
  useEffect(() => {
    if (activeConv && isSubscriber) {
      pollRef.current = setInterval(() => fetchMessages(activeConv.id), 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeConv, isSubscriber, fetchMessages]);

  // ── Poll conversations list ──
  useEffect(() => {
    if (isSubscriber) {
      convPollRef.current = setInterval(fetchConversations, 5000);
      return () => clearInterval(convPollRef.current);
    }
  }, [isSubscriber, fetchConversations]);

  // ── Send message ──
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConv || sendingMsg) return;

    setSendingMsg(true);
    try {
      await axios.post(`${API}/conversations/${activeConv.id}/messages`, { content: newMessage.trim() }, authHeaders());
      setNewMessage('');
      fetchMessages(activeConv.id);
      fetchConversations();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  // ── User search ──
  useEffect(() => {
    if (userSearch.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(userSearch)}`, authHeaders());
        setSearchResults(res.data);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, API, authHeaders]);

  // ── Create conversation ──
  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    try {
      const isGroup = newChatTab === 'group';
      const res = await axios.post(`${API}/conversations`, {
        type: isGroup ? 'group' : 'direct',
        name: isGroup ? groupName : '',
        participant_ids: selectedUsers.map(u => u.id)
      }, authHeaders());

      setShowNewDialog(false);
      setSelectedUsers([]);
      setGroupName('');
      setUserSearch('');
      fetchConversations();

      setActiveConv(res.data);
      navigate(`/chat/${res.data.id}`);
      setMobileShowChat(true);
      toast.success(isGroup ? 'Group created!' : 'Conversation started!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create conversation');
    }
  };

  const selectConversation = (conv) => {
    setActiveConv(conv);
    navigate(`/chat/${conv.id}`);
    setMobileShowChat(true);
  };

  const toggleUserSelection = (u) => {
    if (selectedUsers.find(s => s.id === u.id)) {
      setSelectedUsers(selectedUsers.filter(s => s.id !== u.id));
    } else {
      if (newChatTab === 'direct') {
        setSelectedUsers([u]);
      } else {
        setSelectedUsers([...selectedUsers, u]);
      }
    }
  };

  const filteredConvs = conversations.filter(conv => {
    if (!searchConvs) return true;
    const q = searchConvs.toLowerCase();
    if (conv.name?.toLowerCase().includes(q)) return true;
    return Object.values(conv.participant_names || {}).some(n => n.toLowerCase().includes(q));
  });

  // ── Not subscribed gate ──
  if (!isSubscriber) {
    return (
      <div className="min-h-screen bg-[#F2F7F4] texture-grain">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-[2rem] p-12 shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="chat-subscribe-gate">
            <Lock size={48} className="text-[#4B6B58] mx-auto mb-6" />
            <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#1B4332] mb-3">Subscriber Chat</h2>
            <p className="text-[#4B6B58] mb-8 max-w-md mx-auto">
              Connect with other horse owners and share tips. Chat is available for subscribers.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              data-testid="chat-subscribe-button"
              className="px-8 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
            >
              Subscribe — $9.95/mo
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
        <div className="flex-1 flex gap-4 min-h-0 animate-fade-in">

          {/* ── Left Sidebar: Conversation List ── */}
          <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] flex-shrink-0 flex-col bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(27,67,50,0.06)] overflow-hidden`}>
            {/* Sidebar Header */}
            <div className="p-4 border-b border-[#D1E0D5]">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332]" data-testid="chat-title">Messages</h2>
                <button
                  onClick={() => setShowNewDialog(true)}
                  data-testid="new-chat-button"
                  className="w-9 h-9 rounded-full bg-[#1B4332] text-white flex items-center justify-center btn-lift hover:bg-[#2D6A4F]"
                >
                  <Plus size={18} />
                </button>
              </div>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
                <input
                  value={searchConvs}
                  onChange={e => setSearchConvs(e.target.value)}
                  placeholder="Search conversations..."
                  data-testid="search-conversations"
                  className="stable-input w-full pl-9 py-2 text-sm"
                />
              </div>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conversation-list">
              {loadingConvs ? (
                <div className="p-4 text-center text-[#4B6B58] text-sm animate-pulse">Loading...</div>
              ) : filteredConvs.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle size={32} className="text-[#4B6B58]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#4B6B58]">{searchConvs ? 'No matches found' : 'No conversations yet'}</p>
                  {!searchConvs && (
                    <button
                      onClick={() => setShowNewDialog(true)}
                      data-testid="start-first-chat-button"
                      className="mt-3 text-sm font-semibold text-[#1B4332] hover:underline"
                    >
                      Start a conversation
                    </button>
                  )}
                </div>
              ) : (
                filteredConvs.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conv={conv}
                    currentUserId={user?.id}
                    isActive={activeConv?.id === conv.id}
                    onClick={() => selectConversation(conv)}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Right Panel: Chat Area ── */}
          <div className={`${!mobileShowChat ? 'hidden md:flex' : 'flex'} flex-1 flex-col bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(27,67,50,0.06)] overflow-hidden min-w-0`}>
            {!activeConv ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8" data-testid="no-conversation-selected">
                <div className="w-20 h-20 rounded-full bg-[#E9EFE8] flex items-center justify-center mb-4">
                  <MessageCircle size={32} className="text-[#4B6B58]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">Your Messages</h3>
                <p className="text-[#4B6B58] text-sm text-center max-w-xs mb-4">Select a conversation or start a new one to connect with fellow horse owners.</p>
                <button
                  onClick={() => setShowNewDialog(true)}
                  data-testid="empty-new-chat-button"
                  className="px-6 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift hover:bg-[#2D6A4F]"
                >
                  New Conversation
                </button>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="flex items-center gap-3 p-4 border-b border-[#D1E0D5]" data-testid="chat-header">
                  <button
                    onClick={() => { setMobileShowChat(false); }}
                    className="md:hidden p-1 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"
                    data-testid="chat-back-button"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-[#1B4332]/10 text-[#1B4332]`}>
                    {activeConv.type === 'group'
                      ? <Users size={18} />
                      : (Object.entries(activeConv.participant_names || {}).find(([id]) => id !== user?.id)?.[1] || 'U').slice(0, 2).toUpperCase()
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#122A1F] text-sm truncate">
                      {activeConv.type === 'group'
                        ? (activeConv.name || 'Group Chat')
                        : Object.entries(activeConv.participant_names || {}).find(([id]) => id !== user?.id)?.[1] || 'Unknown'
                      }
                    </h3>
                    {activeConv.type === 'group' && (
                      <p className="text-[10px] text-[#4B6B58] truncate">
                        {Object.values(activeConv.participant_names || {}).join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-[#F2F7F4]/50" data-testid="messages-container">
                  {loadingMsgs ? (
                    <div className="text-center text-[#4B6B58] text-sm py-8 animate-pulse">Loading messages...</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-[#4B6B58] text-sm">No messages yet. Say hello!</p>
                    </div>
                  ) : (
                    messages.map((msg, i) => (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        isOwn={msg.sender_id === user?.id}
                        showName={activeConv.type === 'group' && (i === 0 || messages[i - 1]?.sender_id !== msg.sender_id)}
                      />
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-3 border-t border-[#D1E0D5] flex items-center gap-2" data-testid="message-form">
                  <input
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    data-testid="message-input"
                    className="stable-input flex-1 py-2.5 text-sm"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sendingMsg}
                    data-testid="send-message-button"
                    className="w-10 h-10 rounded-full bg-[#1B4332] text-white flex items-center justify-center btn-lift hover:bg-[#2D6A4F] disabled:opacity-40 flex-shrink-0"
                  >
                    <Send size={16} />
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>

      {/* ── New Conversation Dialog ── */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="new-chat-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">New Conversation</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Start a direct message or create a group chat.</DialogDescription>
          </DialogHeader>

          <Tabs value={newChatTab} onValueChange={(v) => { setNewChatTab(v); setSelectedUsers([]); }}>
            <TabsList className="w-full bg-[#E9EFE8] rounded-xl p-1">
              <TabsTrigger value="direct" data-testid="tab-direct-chat" className="flex-1 rounded-lg data-[state=active]:bg-[#1B4332] data-[state=active]:text-white text-sm">
                <User size={14} className="mr-1.5" /> Direct
              </TabsTrigger>
              <TabsTrigger value="group" data-testid="tab-group-chat" className="flex-1 rounded-lg data-[state=active]:bg-[#1B4332] data-[state=active]:text-white text-sm">
                <Users size={14} className="mr-1.5" /> Group
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 space-y-4">
              {newChatTab === 'group' && (
                <div>
                  <label className="block text-sm font-medium text-[#122A1F] mb-1">Group Name</label>
                  <input
                    value={groupName}
                    onChange={e => setGroupName(e.target.value)}
                    placeholder="Enter group name..."
                    data-testid="group-name-input"
                    className="stable-input w-full"
                  />
                </div>
              )}

              {/* User Search */}
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">
                  {newChatTab === 'direct' ? 'Search User' : 'Add Members'}
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
                  <input
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    data-testid="user-search-input"
                    className="stable-input w-full pl-9"
                  />
                </div>
              </div>

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="selected-users">
                  {selectedUsers.map(u => (
                    <span key={u.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1B4332] text-white text-xs font-medium">
                      {u.name}
                      <button onClick={() => toggleUserSelection(u)} className="hover:bg-white/20 rounded-full p-0.5" data-testid={`remove-selected-${u.id}`}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search Results */}
              <div className="max-h-48 overflow-y-auto space-y-1" data-testid="user-search-results">
                {searching && <div className="text-center text-[#4B6B58] text-sm py-2 animate-pulse">Searching...</div>}
                {!searching && userSearch.length >= 2 && searchResults.length === 0 && (
                  <p className="text-center text-[#4B6B58] text-sm py-2">No subscribers found</p>
                )}
                {searchResults.map(u => {
                  const isSelected = selectedUsers.find(s => s.id === u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => toggleUserSelection(u)}
                      data-testid={`search-result-${u.id}`}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left ${
                        isSelected ? 'bg-[#E9EFE8]' : 'hover:bg-[#F9F7F3]'
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#1B4332]/10 flex items-center justify-center text-xs font-bold text-[#1B4332]">
                        {u.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-[#122A1F] block truncate">{u.name}</span>
                        <span className="text-xs text-[#4B6B58] truncate block">{u.email}</span>
                      </div>
                      {isSelected && <Check size={16} className="text-[#2D6A4F] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleCreateConversation}
                disabled={selectedUsers.length === 0}
                data-testid="create-conversation-button"
                className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F] disabled:opacity-40"
              >
                {newChatTab === 'direct' ? 'Start Chat' : 'Create Group'}
              </button>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
